import React, { useEffect, useState } from 'react';
import { Permissions, useAuth } from '@kp/react-sdk';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AttributeDetails } from './AttributeDetails';
import { useAttributeDetailsQuery } from '../../../__generated__/types';
import { useBreadcrumb } from '../../../contexts/breadcrumb-context';
import { makeHierarchyBreadcrumb } from '../../../utils/breadcrumb';
import * as Entities from '../../../constants/Entities';
import { ErrorMessage } from '../../../components/Errors';
import { entityLocation } from '../../../utils/entity';
import { AttributeDeleteContainer } from '../delete/AttributeDeleteContainer';
import { useHeader } from '../../../contexts/header-context';
import { NotFound } from '../../errorScreens';

export const AttributeDetailsContainer: React.FC = () => {
  const { t } = useTranslation(['attributes', 'general']);
  const navigate = useNavigate();
  const params = useParams();
  const { setTitle } = useHeader();
  const { attributeId } = params;
  const { hasPermission } = useAuth();

  const [deleteMode, setDeleteMode] = useState(false);

  const { data, loading, error } = useAttributeDetailsQuery({
    variables: { attributeId },
  });

  const attribute = data?.attribute;
  const hierarchy = attribute
    ? makeHierarchyBreadcrumb(
        [
          {
            type: Entities.ATTRIBUTE,
            id: attribute.id,
            name: attribute.name || attribute.id,
          },
        ],
        t,
      )
    : [];

  useBreadcrumb(hierarchy);

  const handleDeleteCancel = () => setDeleteMode(false);
  const handleDeleteError = () => setDeleteMode(false);
  const handleDelete = () => setDeleteMode(true);
  const handleDeleteComplete = () =>
    navigate(entityLocation(Entities.ATTRIBUTE, ''));
  const handleEdit = () =>
    navigate(`${entityLocation(Entities.ATTRIBUTE, attributeId)}/edit`);

  useEffect(() => {
    setTitle({ main: attribute?.name });
  }, [setTitle, attribute]);

  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (!loading && !attribute) {
    return (
      <NotFound
        title={t('attributes:details.notFound.title') ?? ''}
        subtitle={t('attributes:details.notFound.subtitle') ?? ''}
        buttonText={t('attributes:details.notFound.buttonText') ?? ''}
        buttonOnClick={() => navigate(Entities.ATTRIBUTE.path)}
      />
    );
  }

  return (
    <>
      <AttributeDetails
        loading={loading}
        name={attribute?.name ?? ''}
        description={attribute?.description ?? ''}
        dataType={attribute?.dataType ?? null}
        required={attribute?.required ?? false}
        isGlobal={attribute?.isGlobal ?? false}
        onDelete={
          hasPermission(Permissions.AttributesWrite) ? handleDelete : undefined
        }
        onEdit={
          hasPermission(Permissions.AttributesWrite) ? handleEdit : undefined
        }
      />
      <AttributeDeleteContainer
        id={attributeId}
        isOpen={deleteMode}
        onCompleted={handleDeleteComplete}
        onCancel={handleDeleteCancel}
        onError={handleDeleteError}
      />
    </>
  );
};
